import { useState, useEffect, useCallback } from 'react';
import { Layout } from '../../components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Label } from '../../components/ui/label';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../../components/ui/collapsible';
import { toast } from 'sonner';
import api from '../../api';
import { useAuth } from '../../context/AuthContext';
import { 
  Package,
  Download,
  Search,
  ChevronDown,
  ChevronRight,
  Loader2,
  ShieldAlert,
  Monitor,
  Server,
  ChevronLeft,
  Building2,
  Users,
  HardDrive,
  X
} from 'lucide-react';

const SoftwareReport = () => {
  const { can } = useAuth();
  const canRead = can('reports.software', 'read');

  // Estados principales
  const [source, setSource] = useState('all'); // 'all', 'workstations', 'servers'
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Filtros
  const [filters, setFilters] = useState({
    search: '',
    publisher: '',
    site_id: '',
    empresa_id: '',
    cliente_id: ''
  });
  const [filterOptions, setFilterOptions] = useState({
    sites: [],
    empresas: [],
    clientes: []
  });
  const [publishers, setPublishers] = useState([]);
  const [publisherSearch, setPublisherSearch] = useState('');

  // Datos
  const [stats, setStats] = useState({ 
    total_installs: 0, 
    unique_software: 0, 
    unique_publishers: 0, 
    total_devices: 0 
  });
  const [softwareList, setSoftwareList] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  
  // Detalle expandido
  const [expandedSoftware, setExpandedSoftware] = useState(null);
  const [detailData, setDetailData] = useState({ items: [], total: 0, page: 1, pages: 1, versions: [] });
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailSearch, setDetailSearch] = useState('');
  const [detailPage, setDetailPage] = useState(1);

  // Cargar opciones de filtro
  const fetchFilterOptions = useCallback(async () => {
    try {
      const response = await api.get('/reports/software/filter-options');
      setFilterOptions(response.data);
    } catch (error) {
      console.error('Error fetching filter options:', error);
    }
  }, []);

  // Cargar publishers
  const fetchPublishers = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      params.append('source', source);
      if (publisherSearch) params.append('search', publisherSearch);
      
      const response = await api.get(`/reports/software/publishers?${params}`);
      setPublishers(response.data.publishers || []);
    } catch (error) {
      console.error('Error fetching publishers:', error);
    }
  }, [source, publisherSearch]);

  // Cargar lista de software
  const fetchSoftwareList = useCallback(async (pageNum = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('source', source);
      params.append('page', pageNum.toString());
      params.append('page_size', '50');
      
      if (filters.search) params.append('search', filters.search);
      if (filters.publisher) params.append('publisher', filters.publisher);
      if (filters.site_id) params.append('site_id', filters.site_id);
      if (filters.empresa_id) params.append('empresa_id', filters.empresa_id);
      if (filters.cliente_id) params.append('cliente_id', filters.cliente_id);

      const response = await api.get(`/reports/software/list?${params}`);
      setStats(response.data.stats);
      setSoftwareList(response.data.items);
      setTotal(response.data.total);
      setTotalPages(response.data.pages);
      setPage(response.data.page);
      setExpandedSoftware(null);
    } catch (error) {
      console.error('Error fetching software list:', error);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  }, [source, filters]);

  // Cargar detalle de un software
  const fetchSoftwareDetail = async (softwareName, publisher, pageNum = 1) => {
    setDetailLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('software_name', softwareName);
      params.append('source', source);
      params.append('page', pageNum.toString());
      params.append('page_size', '10');
      
      if (publisher) params.append('publisher', publisher);
      if (filters.site_id) params.append('site_id', filters.site_id);
      if (filters.empresa_id) params.append('empresa_id', filters.empresa_id);
      if (filters.cliente_id) params.append('cliente_id', filters.cliente_id);
      if (detailSearch) params.append('search', detailSearch);

      const response = await api.get(`/reports/software/detail?${params}`);
      setDetailData(response.data);
      setDetailPage(pageNum);
    } catch (error) {
      console.error('Error fetching detail:', error);
      toast.error('Error al cargar detalle');
    } finally {
      setDetailLoading(false);
    }
  };

  // Exportar a Excel
  const handleExport = async (softwareName = null, publisher = null) => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      params.append('source', source);
      
      if (softwareName) {
        params.append('software_name', softwareName);
        if (publisher) params.append('publisher', publisher);
      } else {
        if (filters.search) params.append('search', filters.search);
        if (filters.publisher) params.append('publisher', filters.publisher);
      }
      if (filters.site_id) params.append('site_id', filters.site_id);
      if (filters.empresa_id) params.append('empresa_id', filters.empresa_id);
      if (filters.cliente_id) params.append('cliente_id', filters.cliente_id);

      const response = await api.get(`/reports/software/export?${params}`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const filename = softwareName 
        ? `software_${softwareName.substring(0, 30).replace(/\s+/g, '_')}.xlsx`
        : 'reporte_software_instalado.xlsx';
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Reporte exportado exitosamente');
    } catch (error) {
      console.error('Error exporting:', error);
      toast.error('Error al exportar');
    } finally {
      setExporting(false);
    }
  };

  // Toggle expandir software
  const toggleSoftware = (software) => {
    const key = `${software.software_name}|${software.publisher || ''}`;
    if (expandedSoftware === key) {
      setExpandedSoftware(null);
    } else {
      setExpandedSoftware(key);
      setDetailSearch('');
      setDetailPage(1);
      fetchSoftwareDetail(software.software_name, software.publisher);
    }
  };

  // Clear filters
  const clearFilters = () => {
    setFilters({
      search: '',
      publisher: '',
      site_id: '',
      empresa_id: '',
      cliente_id: ''
    });
  };

  // Effects
  useEffect(() => {
    if (canRead) {
      fetchFilterOptions();
    }
  }, [canRead, fetchFilterOptions]);

  useEffect(() => {
    if (canRead) {
      fetchPublishers();
    }
  }, [source, publisherSearch, canRead, fetchPublishers]);

  useEffect(() => {
    if (canRead) {
      setPage(1);
      fetchSoftwareList(1);
    }
  }, [source, filters, canRead, fetchSoftwareList]);

  // Source icon helper
  const getSourceIcon = (sourceType) => {
    if (sourceType === 'Servidor') {
      return <Server className="h-4 w-4 text-blue-500" />;
    }
    return <Monitor className="h-4 w-4 text-green-500" />;
  };

  const hasActiveFilters = filters.search || filters.publisher || filters.site_id || filters.empresa_id || filters.cliente_id;

  return (
    <Layout>
      <div className="space-y-6" data-testid="software-report-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Package className="h-8 w-8 text-primary" strokeWidth={1.5} />
            <div>
              <h1 className="text-2xl font-semibold">Reporte de Software Instalado</h1>
              <p className="text-sm text-muted-foreground">Auditoría de aplicaciones instaladas en Workstations y Servidores</p>
            </div>
          </div>
        </div>

        {/* Sin permiso */}
        {!canRead && (
          <Card className="border-destructive/50 bg-destructive/5">
            <CardContent className="flex items-center gap-4 py-8">
              <ShieldAlert className="h-12 w-12 text-destructive" strokeWidth={1.5} />
              <div>
                <h3 className="text-lg font-medium text-destructive">Acceso Denegado</h3>
                <p className="text-sm text-muted-foreground">No tiene permisos para acceder a este reporte.</p>
              </div>
            </CardContent>
          </Card>
        )}

        {canRead && (
          <>
            {/* Selector de Fuente */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Fuente de Datos</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex gap-2">
                  <Button
                    variant={source === 'all' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setSource('all')}
                    className="gap-2"
                    data-testid="source-all"
                  >
                    <HardDrive className="h-4 w-4" />
                    Todos
                  </Button>
                  <Button
                    variant={source === 'workstations' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setSource('workstations')}
                    className="gap-2"
                    data-testid="source-workstations"
                  >
                    <Monitor className="h-4 w-4" />
                    Workstations
                  </Button>
                  <Button
                    variant={source === 'servers' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setSource('servers')}
                    className="gap-2"
                    data-testid="source-servers"
                  >
                    <Server className="h-4 w-4" />
                    Servidores
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Filtros */}
            <Card>
              <CardContent className="pt-6">
                <div className="flex flex-wrap gap-4 items-end">
                  <div className="flex-1 min-w-[200px]">
                    <Label className="text-xs">Buscar Software</Label>
                    <div className="relative">
                      <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="Nombre del software..."
                        value={filters.search}
                        onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                        className="pl-8"
                        data-testid="search-software"
                      />
                    </div>
                  </div>
                  
                  <div className="w-[180px]">
                    <Label className="text-xs">Proveedor</Label>
                    <Select
                      value={filters.publisher || "todos"}
                      onValueChange={(v) => setFilters(prev => ({ ...prev, publisher: v === "todos" ? "" : v }))}
                    >
                      <SelectTrigger data-testid="filter-publisher">
                        <SelectValue placeholder="Todos" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="todos">Todos</SelectItem>
                        {publishers.slice(0, 50).map((p, idx) => (
                          <SelectItem key={idx} value={p}>{p}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="w-[150px]">
                    <Label className="text-xs">Site</Label>
                    <Select
                      value={filters.site_id || "todos"}
                      onValueChange={(v) => setFilters(prev => ({ ...prev, site_id: v === "todos" ? "" : v }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Todos" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="todos">Todos</SelectItem>
                        {filterOptions.sites?.map(s => (
                          <SelectItem key={s.id} value={s.id.toString()}>{s.nombre}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {source !== 'servers' && (
                    <>
                      <div className="w-[150px]">
                        <Label className="text-xs">Empresa</Label>
                        <Select
                          value={filters.empresa_id || "todos"}
                          onValueChange={(v) => setFilters(prev => ({ ...prev, empresa_id: v === "todos" ? "" : v }))}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Todas" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="todos">Todas</SelectItem>
                            {filterOptions.empresas?.map(e => (
                              <SelectItem key={e.id} value={e.id.toString()}>{e.nombre}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="w-[150px]">
                        <Label className="text-xs">Cliente</Label>
                        <Select
                          value={filters.cliente_id || "todos"}
                          onValueChange={(v) => setFilters(prev => ({ ...prev, cliente_id: v === "todos" ? "" : v }))}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Todos" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="todos">Todos</SelectItem>
                            {filterOptions.clientes?.map(c => (
                              <SelectItem key={c.id} value={c.id.toString()}>{c.nombre}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </>
                  )}

                  {hasActiveFilters && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={clearFilters}
                      className="gap-1 text-muted-foreground"
                    >
                      <X className="h-4 w-4" />
                      Limpiar
                    </Button>
                  )}

                  <Button
                    onClick={() => handleExport()}
                    disabled={exporting || softwareList.length === 0}
                    variant="outline"
                    className="gap-2"
                    data-testid="export-btn"
                  >
                    {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                    Exportar
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="text-2xl font-bold">{stats.total_installs.toLocaleString()}</div>
                  <p className="text-xs text-muted-foreground">Instalaciones Totales</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="text-2xl font-bold">{stats.unique_software.toLocaleString()}</div>
                  <p className="text-xs text-muted-foreground">Software Único</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="text-2xl font-bold">{stats.unique_publishers.toLocaleString()}</div>
                  <p className="text-xs text-muted-foreground">Proveedores</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="text-2xl font-bold">{stats.total_devices.toLocaleString()}</div>
                  <p className="text-xs text-muted-foreground">Equipos con Software</p>
                </CardContent>
              </Card>
            </div>

            {/* Tabla de Software */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Software Instalado</CardTitle>
                <CardDescription>
                  {total.toLocaleString()} aplicaciones encontradas. Click en una fila para ver detalle de equipos.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                  </div>
                ) : softwareList.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    No se encontró software con los filtros aplicados
                  </div>
                ) : (
                  <div className="space-y-2">
                    {softwareList.map((software, idx) => {
                      const key = `${software.software_name}|${software.publisher || ''}`;
                      const isExpanded = expandedSoftware === key;
                      
                      return (
                        <Collapsible key={idx} open={isExpanded}>
                          <CollapsibleTrigger asChild>
                            <div
                              className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-colors ${
                                isExpanded 
                                  ? 'bg-primary/10 border border-primary/20' 
                                  : 'bg-muted/50 hover:bg-muted'
                              }`}
                              onClick={() => toggleSoftware(software)}
                              data-testid={`software-row-${idx}`}
                            >
                              <div className="flex items-center gap-3 flex-1 min-w-0">
                                {isExpanded ? (
                                  <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
                                ) : (
                                  <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                                )}
                                <Package className="h-4 w-4 text-primary shrink-0" />
                                <div className="min-w-0 flex-1">
                                  <span className="font-medium block truncate">{software.software_name}</span>
                                  {software.publisher && (
                                    <span className="text-xs text-muted-foreground block truncate">
                                      {software.publisher}
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-4 shrink-0">
                                <div className="text-right">
                                  <span className="text-sm font-medium">{software.install_count}</span>
                                  <span className="text-xs text-muted-foreground ml-1">inst.</span>
                                </div>
                                <div className="text-right">
                                  <span className="text-sm">{software.unique_devices}</span>
                                  <span className="text-xs text-muted-foreground ml-1">equipos</span>
                                </div>
                                {software.versions && (
                                  <span className="text-xs text-muted-foreground max-w-[150px] truncate hidden md:block">
                                    {software.versions.split(',').length > 1 
                                      ? `${software.versions.split(',').length} versiones`
                                      : software.versions
                                    }
                                  </span>
                                )}
                              </div>
                            </div>
                          </CollapsibleTrigger>
                          
                          <CollapsibleContent>
                            <div className="mt-2 ml-6 p-4 bg-background border rounded-lg">
                              {/* Versiones badge */}
                              {detailData.versions && detailData.versions.length > 0 && (
                                <div className="mb-4 flex flex-wrap gap-2">
                                  <span className="text-xs text-muted-foreground">Versiones:</span>
                                  {detailData.versions.slice(0, 10).map((v, vidx) => (
                                    <span key={vidx} className="px-2 py-0.5 bg-muted rounded-full text-xs">
                                      {v.version || 'Sin versión'} ({v.count})
                                    </span>
                                  ))}
                                  {detailData.versions.length > 10 && (
                                    <span className="text-xs text-muted-foreground">
                                      +{detailData.versions.length - 10} más
                                    </span>
                                  )}
                                </div>
                              )}
                              
                              {/* Buscador y exportar dentro del detalle */}
                              <div className="flex gap-2 mb-4">
                                <Input
                                  placeholder="Buscar equipo o usuario..."
                                  value={detailSearch}
                                  onChange={(e) => setDetailSearch(e.target.value)}
                                  className="max-w-xs"
                                />
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => fetchSoftwareDetail(software.software_name, software.publisher)}
                                >
                                  <Search className="h-4 w-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleExport(software.software_name, software.publisher)}
                                  disabled={exporting}
                                >
                                  <Download className="h-4 w-4" />
                                </Button>
                              </div>
                              
                              {detailLoading ? (
                                <div className="flex items-center justify-center py-8">
                                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                                </div>
                              ) : (
                                <>
                                  <div className="overflow-x-auto">
                                    <table className="w-full text-sm">
                                      <thead>
                                        <tr className="border-b">
                                          <th className="text-left py-2 px-3 font-medium">Tipo</th>
                                          <th className="text-left py-2 px-3 font-medium">Equipo</th>
                                          <th className="text-left py-2 px-3 font-medium">Versión</th>
                                          <th className="text-left py-2 px-3 font-medium">Usuario</th>
                                          <th className="text-left py-2 px-3 font-medium">Legajo</th>
                                          <th className="text-left py-2 px-3 font-medium">Site</th>
                                          <th className="text-left py-2 px-3 font-medium">Estado</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {detailData.items.map((item, detailIdx) => (
                                          <tr key={detailIdx} className="border-b last:border-0 hover:bg-muted/50">
                                            <td className="py-2 px-3">
                                              <div className="flex items-center gap-1">
                                                {getSourceIcon(item.tipo)}
                                                <span className="text-xs">{item.tipo}</span>
                                              </div>
                                            </td>
                                            <td className="py-2 px-3 font-mono text-xs">{item.equipo}</td>
                                            <td className="py-2 px-3 text-xs">{item.version || '-'}</td>
                                            <td className="py-2 px-3 text-muted-foreground">{item.usuario || '-'}</td>
                                            <td className="py-2 px-3 text-muted-foreground">{item.legajo || '-'}</td>
                                            <td className="py-2 px-3 text-muted-foreground">{item.site_name || '-'}</td>
                                            <td className="py-2 px-3">
                                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                                item.status_name?.toLowerCase().includes('activo')
                                                  ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                                                  : 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400'
                                              }`}>
                                                {item.status_name || '-'}
                                              </span>
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                  
                                  {/* Paginación del detalle */}
                                  {detailData.pages > 1 && (
                                    <div className="flex items-center justify-between mt-4 pt-4 border-t">
                                      <span className="text-sm text-muted-foreground">
                                        Mostrando {((detailData.page - 1) * 10) + 1}-{Math.min(detailData.page * 10, detailData.total)} de {detailData.total}
                                      </span>
                                      <div className="flex gap-2">
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          onClick={() => fetchSoftwareDetail(software.software_name, software.publisher, detailPage - 1)}
                                          disabled={detailPage <= 1}
                                        >
                                          <ChevronLeft className="h-4 w-4" />
                                        </Button>
                                        <span className="flex items-center px-2 text-sm">
                                          {detailData.page} / {detailData.pages}
                                        </span>
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          onClick={() => fetchSoftwareDetail(software.software_name, software.publisher, detailPage + 1)}
                                          disabled={detailPage >= detailData.pages}
                                        >
                                          <ChevronRight className="h-4 w-4" />
                                        </Button>
                                      </div>
                                    </div>
                                  )}
                                </>
                              )}
                            </div>
                          </CollapsibleContent>
                        </Collapsible>
                      );
                    })}
                  </div>
                )}

                {/* Paginación principal */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between mt-6 pt-4 border-t">
                    <span className="text-sm text-muted-foreground">
                      Página {page} de {totalPages} ({total.toLocaleString()} resultados)
                    </span>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => fetchSoftwareList(page - 1)}
                        disabled={page <= 1 || loading}
                      >
                        <ChevronLeft className="h-4 w-4" />
                        Anterior
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => fetchSoftwareList(page + 1)}
                        disabled={page >= totalPages || loading}
                      >
                        Siguiente
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}

                {/* Leyenda */}
                <div className="flex items-center gap-6 mt-6 pt-4 border-t text-sm text-muted-foreground">
                  <div className="flex items-center gap-2">
                    <Monitor className="h-4 w-4 text-green-500" />
                    <span>Workstation</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Server className="h-4 w-4 text-blue-500" />
                    <span>Servidor</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </Layout>
  );
};

export default SoftwareReport;
